import * as THREE from 'three';
import { cragLayout, hexToWorld, passFlow, passLayout, QUARTER, type BattleState, type MapDef, type PassLayout } from '@somnhold/sim';
import type { TerrainBuild } from './terrain.ts';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { COMPANY_RED } from '../models/sendas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The Pass of the Rift (docs/sendas/guerrero.md, chapter 5), drawn on the Spine's snow (engine/spine.ts repaints the
 * board and lights it, always cold).
 *
 * - `crag` (act 1, and the opening): the ledge of the wind up a sheer cliff, the wall's slate on the west in tall
 *   columns with niches cut into it (a lintel of rock over each, its floor in a colder shade: where the wind misses),
 *   the drop on the east cut out of the board, a valley of cloud far below; at the top, the pass's mouth between two
 *   plates of split ice. A gust shows as the snow lifting off the ledge, streaming toward the drop (one sheet, a
 *   shader), stronger as it comes.
 * - `pass` and `passFire` (acts 2 to 4, the speech, the close; the dusk and the fire) and `passFar` (the cinematic):
 *   the gorge between two plates of the titan's shell, its walls tall slate columns, the split ice high at its head.
 *   Across its foot Ruy's line: eleven sockets, the shields of the Company standing in them (one instanced mesh), their
 *   red crosses lit red as their names are said or the line comes whole (glows). The camp's fire at slot 1's end, the
 *   stretcher, the wedges' pile; up the snow side's wall the beam, its basket and stones, and Ruy's cornice hanging over
 *   the pass. Three rocks with their grain for the Ventisquero's avalanche; its lanes' shadow when it is called; the
 *   footprints of the next charge sinking into the snow, a wedge in one; the sweep's ring; Harl's shield.
 *
 * No particles by CPU, no lights of its own (docs/RENDIMIENTO.md): glows, two shader sheets (the gust, the lanes), two
 * instanced meshes (the shields, the stones and wedges). Draw calls: the dressing (merged), the shields, the small
 * pieces, the cornice, the sheets, the decals (prints, sweep), the glow layer.
 */

export const PASS_PLACES = new Set(['passFar', 'crag', 'pass', 'passFire']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const SNOW = new THREE.Color('#eef2f8');
const SNOW_D = '#c4d0de';
const SLATE = ['#4a5361', '#3e4653', '#56606e', '#353c48', '#2d333d'];
const ICE = ['#a9c6dc', '#8fb3cf', '#c4daea', '#7ea4c4'];
const WOOD = ['#3a3430', '#4a4038', '#2e2925'];
const IRON = '#3b3f46';
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const FIRE = new THREE.Color('#ff9a5a');
const COLD = new THREE.Color('#8fc4ff');

/** The gust's sheet over the ledge: streaks of snow lifting and racing toward the drop (+x), `uK` from 0 to 1. */
const gustMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { uTime: { value: 0 }, uK: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() {
        vec4 w = modelMatrix * vec4(position + normal * 0.06, 1.0);
        vXZ = w.xz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uK;
      varying vec2 vXZ;
      // (Wrapped: with large arguments the sin hash loses precision and the noise shows in blocks, docs/RENDIMIENTO.md.)
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float a = n(vec2(vXZ.x * 0.6 - uTime * 6.0, vXZ.y * 3.2)) * 0.6 + n(vec2(vXZ.x * 1.4 - uTime * 9.0, vXZ.y * 7.0)) * 0.4;
        float streak = smoothstep(0.45, 0.9, a);
        gl_FragColor = vec4(mix(vec3(0.62, 0.72, 0.88), vec3(1.0), streak), streak * uK * 0.75);
      }`,
  });

/** The avalanche's shadow over the lanes (act 4): a cold veil per lane with snow, red where it reaches the line. */
const lanesMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 }, uK: { value: 0 }, uLanes: { value: new Array(7).fill(0) }, uHit: { value: new Array(7).fill(0) }, uX0: { value: 0 }, uLaneW: { value: 1 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        vec4 w = modelMatrix * vec4(position, 1.0);
        vXZ = w.xz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uK;
      uniform float uLanes[7];
      uniform float uHit[7];
      uniform float uX0;
      uniform float uLaneW;
      varying vec2 vXZ;
      varying vec2 vUv;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float k = floor((vXZ.x - uX0) / uLaneW);
        if (k < 0.0 || k > 6.0) discard;
        int i = int(k);
        float t = uLanes[i];
        float hit = uHit[i];
        if (t < 0.01) discard;
        float f = fract((vXZ.x - uX0) / uLaneW);
        float edge = smoothstep(0.0, 0.12, f) * smoothstep(1.0, 0.88, f);
        float s = n(vec2(vXZ.x * 2.2, vXZ.y * 0.7 - uTime * 1.6)) * 0.5 + 0.5;
        vec3 col = mix(vec3(0.36, 0.46, 0.64), vec3(1.0, 0.31, 0.23), hit * 0.75);
        gl_FragColor = vec4(col, uK * edge * (0.35 + 0.25 * s) * min(1.0, 0.5 + t * 0.4));
      }`,
  });

export class PassPlace {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  private glows: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number }[] = [];
  private now = 0;
  private L: PassLayout | null = null;
  /** Where the HUD hangs its marks (world points). */
  readonly anchors: {
    slots: THREE.Vector3[];
    stretcher: THREE.Vector3 | null;
    ruy: THREE.Vector3 | null;
    stones: THREE.Vector3[];
    basket: THREE.Vector3 | null;
    rocks: THREE.Vector3[];
    pile: THREE.Vector3 | null;
    banner: THREE.Vector3 | null;
    prints: THREE.Vector3[];
    shield: THREE.Vector3 | null;
    niches: THREE.Vector3[];
  } = { slots: [], stretcher: null, ruy: null, stones: [], basket: null, rocks: [], pile: null, banner: null, prints: [new THREE.Vector3(), new THREE.Vector3()], shield: null, niches: [] };
  private shields: THREE.InstancedMesh | null = null;
  private bits: THREE.InstancedMesh | null = null;
  private cornice: THREE.Group | null = null;
  private corniceFall = -1;
  private corniceGone = false;
  private gust: THREE.ShaderMaterial | null = null;
  private gustK = 0;
  private lanes: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial } | null = null;
  private lanesK = 0;
  private prints: THREE.Mesh[] = [];
  private sweep: THREE.Mesh | null = null;
  /** Shields lit red (by slot), and when the whole line lit. */
  private lit: number[] = [];
  private whole = -1;
  private flash = 0;
  private state = { gustWarn: false, gustOn: false, sweep: false, sx: 0, sy: 0, prints: false, fall: false };
  private m = new THREE.Matrix4();
  private c = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
    kit: Kit,
  ) {
    this.group.name = `pass-${place}`;
    if (place === 'crag') this.buildCrag(kit, s);
    else this.buildPass(kit, s, place === 'passFar');
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private std(color?: THREE.ColorRepresentation, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    const m = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0.04, flatShading: true, ...(color !== undefined ? { color } : { vertexColors: true }), ...o });
    this.mats.push(m);
    return m;
  }

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number): void {
    this.glows.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.glows.length * 1.37 });
  }

  /** A column of slate: the wall of the cliff or the gorge, snow caught on its ledges. */
  private column(kit: Kit, geo: THREE.BufferGeometry, cap: THREE.BufferGeometry, x: number, z: number, hgt: number, k: number): void {
    const g = this.ctx.ground(x, z);
    kit.add(geo, SLATE[Math.floor(k * 4.99)] as string, place(x, g + hgt * 0.5, z, k * 5, new THREE.Vector3(1.05 + k * 0.25, hgt, 1.0 + (1 - k) * 0.2)));
    kit.add(cap, SNOW, place(x + 0.05, g + hgt + 0.06, z, k * 7, new THREE.Vector3(0.9, 0.22, 0.85)));
    if (k > 0.55) kit.add(cap, SNOW_D, place(x - 0.2, g + hgt * 0.55, z + 0.4, k * 3, new THREE.Vector3(0.5, 0.12, 0.35)));
  }

  /** Two plates of split ice: the pass's mouth (the crack between them dark blue). */
  private splitIce(kit: Kit, x: number, z: number, wide: number, tall: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const g = this.ctx.ground(x, z);
    for (const k of [-1, 1]) {
      kit.add(box, ICE[1] as string, place(x + k * wide * 0.55, g + tall * 0.5, z, k * 0.15, new THREE.Vector3(wide, tall, 1.3), 0, k * -0.08));
      kit.add(box, ICE[2] as string, place(x + k * wide * 0.5, g + tall * 0.75, z + 0.5, k * 0.2, new THREE.Vector3(wide * 0.7, tall * 0.4, 0.4), 0.1, k * -0.1));
      kit.add(box, SNOW, place(x + k * wide * 0.55, g + tall + 0.1, z, k * 0.15, new THREE.Vector3(wide * 1.02, 0.25, 1.35)));
    }
    kit.add(box, '#16233a', place(x, g + tall * 0.45, z - 0.4, 0, new THREE.Vector3(wide * 0.18, tall * 0.9, 0.3)));
    this.light(new THREE.Vector3(x, g + tall * 0.4, z + 0.2), '#6fa6e0', wide * 2.2, 0.18);
    box.dispose();
  }

  /** The Company's shield, upright, its face to +z (a kit's worth of boxes: wood, rim, the red cross). */
  private shieldGeo(): THREE.BufferGeometry | null {
    const k = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    k.add(box, '#8e8a84', place(0, 0.3, 0, 0, new THREE.Vector3(0.42, 0.46, 0.05)));
    k.add(box, '#8e8a84', place(0, 0.04, 0, Math.PI / 4, new THREE.Vector3(0.3, 0.3, 0.05)));
    k.add(box, '#2b2f36', place(0, 0.3, -0.03, 0, new THREE.Vector3(0.46, 0.5, 0.03)));
    k.add(box, COMPANY_RED, place(0, 0.26, 0.035, 0, new THREE.Vector3(0.07, 0.5, 0.02)));
    k.add(box, COMPANY_RED, place(0, 0.36, 0.037, 0, new THREE.Vector3(0.38, 0.07, 0.02)));
    // Its foot driven into the snow.
    k.add(box, '#5a5550', place(0, -0.1, 0, 0, new THREE.Vector3(0.06, 0.3, 0.06)));
    const mat = this.std(undefined);
    const mesh = k.build(mat, 'pass-shield');
    box.dispose();
    return mesh ? mesh.geometry : null;
  }

  /* ---------- the ledge of the wind ---------- */

  private buildCrag(kit: Kit, s: BattleState): void {
    const L = cragLayout(s.seed);
    const prism = new THREE.CylinderGeometry(0.45, 0.52, 1, 6);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const p of L.way) this.ledgeX.set(Math.round(p.y / 866), p.x);
    // The wall: tall slate columns, taller the farther from the ledge (the cliff goes up).
    const niche = new Set(L.niches.map((h) => `${h.q},${h.r}`));
    const ledge = new Set(L.ledge.map((h) => `${h.q},${h.r}`));
    for (const h of L.wall) {
      const p = this.hex(h.q, h.r);
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      const byLedge = [[1, 0], [1, -1], [0, 1]].some(([dq, dr]) => ledge.has(`${h.q + (dq as number)},${h.r + (dr as number)}`) || niche.has(`${h.q + (dq as number)},${h.r + (dr as number)}`));
      this.column(kit, prism, cap, p.x, p.z, byLedge ? 1.3 + k * 0.7 : 1.9 + k * 1.3, Math.min(0.55, k));
    }
    // The niches: a lintel of rock over each, its floor a colder blue (the wind misses it).
    for (const h of L.niches) {
      const p = this.hex(h.q, h.r);
      const g = this.ctx.ground(p.x, p.z);
      kit.add(box, SLATE[3] as string, place(p.x - 0.1, g + 1.9, p.z, 0.1, new THREE.Vector3(1.3, 0.45, 1.1)));
      kit.add(cap, SNOW, place(p.x - 0.1, g + 2.2, p.z, 1, new THREE.Vector3(1.1, 0.2, 0.9)));
      kit.add(box, '#7f98b8', place(p.x, g + 0.02, p.z, 0, new THREE.Vector3(0.82, 0.02, 0.82)));
      this.light(new THREE.Vector3(p.x, g + 0.6, p.z), '#6f9ad8', 1.6, 0.14);
      this.anchors.niches.push(new THREE.Vector3(p.x, g + 1.2, p.z));
    }
    // The drop's lip: the cliff face going down from the ledge's east edge (slate, ice in its seams, snow on the lip),
    // and far below, a valley of cold cloud.
    const lipX = (r: number): number => ((this.ledgeX.get(r) ?? 0) + 1100) * K;
    for (let r = -8; r <= 8; r++) {
      const z = r * 866 * K;
      const x = lipX(r);
      const k = Math.sin(r * 3.3) * 0.5 + 0.5;
      kit.add(box, SLATE[3 + (r & 1)] as string, place(x + 0.2, -2.9, z, 0, new THREE.Vector3(0.5, 5.4, 0.866 * S + 0.08)));
      kit.add(box, SLATE[3] as string, place(x + 0.55, -6.5, z + 0.2, 0.1, new THREE.Vector3(0.6, 4, 0.9 * S)));
      kit.add(cap, SNOW, place(x - 0.05, this.ctx.ground(x - 0.2, z) + 0.02, z, k * 4, new THREE.Vector3(0.45, 0.16, 0.9)));
      if (k > 0.6) kit.add(prism, ICE[1] as string, place(x + 0.42, -1.2, z + 0.3, k, new THREE.Vector3(0.12, 1.6, 0.12)));
    }
    const cloud = new THREE.Mesh(this.track(new THREE.PlaneGeometry(90, 90)), this.track(new THREE.MeshBasicMaterial({ color: '#4f5e77', transparent: true, opacity: 0.92, depthWrite: false })));
    cloud.rotation.x = -Math.PI / 2;
    cloud.position.set(48, -9, 0);
    this.group.add(cloud);
    const mist = new THREE.Mesh(this.track(new THREE.PlaneGeometry(90, 90)), this.track(new THREE.MeshBasicMaterial({ color: '#8d9db4', transparent: true, opacity: 0.35, depthWrite: false })));
    mist.rotation.x = -Math.PI / 2;
    mist.position.set(48, -4, 0);
    this.group.add(mist);
    // The mouth: the pass between its split ice.
    const m = this.ctx.at(L.mouth.x, L.mouth.y - 1600, 0);
    this.splitIce(kit, m.x, m.z, 1.8, 4.2);
    this.gust = gustMat();
    this.track(this.gust);
    prism.dispose();
    cap.dispose();
    box.dispose();
  }

  /** The drop east of the ledge (world point): past the ledge's east edge on its row, and on beyond the board. */
  private voidAt = (x: number, z: number): boolean => {
    if (!this.ledgeX.size) return false;
    const sx = x / K;
    const row = Math.round(z / K / 866);
    const r = Math.max(-8, Math.min(8, row));
    const edge = this.ledgeX.get(r) ?? 0;
    return sx > edge + 1100 && sx < 40000;
  };
  private ledgeX = new Map<number, number>();

  /** The drop on the ledge: the board's ground over it (and the ground beyond, and what stands on it) cut away. */
  carve(terrain: TerrainBuild): void {
    if (!this.ledgeX.size) return;
    const cut = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!pos) return;
      mesh.updateMatrixWorld(true);
      const mw = mesh.matrixWorld;
      const v = new THREE.Vector3();
      const idx = geo.index;
      const tri = (i: number): number => (idx ? idx.getX(i) : i);
      const n = idx ? idx.count : pos.count;
      const keep: number[] = [];
      for (let i = 0; i < n; i += 3) {
        let cx = 0;
        let cz = 0;
        for (let k = 0; k < 3; k++) {
          v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
          cx += v.x;
          cz += v.z;
        }
        const out = this.voidAt(cx / 3, cz / 3);
        if (idx) {
          if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
        } else if (out) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);
          pos.setXYZ(i + 1, x, y, z);
          pos.setXYZ(i + 2, x, y, z);
        }
      }
      if (idx) geo.setIndex(keep);
      pos.needsUpdate = true;
      geo.computeBoundingSphere();
    };
    cut(terrain.mesh);
    const mm = new THREE.Matrix4();
    const p = new THREE.Vector3();
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') cut(o as THREE.Mesh);
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh) return;
      let touched = false;
      for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, mm);
        p.setFromMatrixPosition(mm);
        if (this.voidAt(p.x, p.z) || this.voidAt(p.x - 0.5, p.z)) {
          im.setMatrixAt(i, zero);
          touched = true;
        }
      }
      if (touched) im.instanceMatrix.needsUpdate = true;
    });
  }

  /** The gust's sheet on the ledge's own ground. */
  sheetOn(src: THREE.Mesh): void {
    if (!this.gust) return;
    const sheet = new THREE.Mesh(src.geometry, this.gust);
    sheet.matrixAutoUpdate = false;
    src.updateMatrixWorld(true);
    sheet.matrix.copy(src.matrixWorld);
    sheet.renderOrder = 3;
    sheet.frustumCulled = false;
    sheet.name = 'pass-gust';
    this.group.add(sheet);
  }

  /* ---------- the pass ---------- */

  private buildPass(kit: Kit, s: BattleState, far: boolean): void {
    // The scenes stand on the fight's board of the same seed the cinematic picks (client game/cine.ts).
    const L = passLayout(s.seed);
    this.L = L;
    const prism = new THREE.CylinderGeometry(0.45, 0.52, 1, 6);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cone = new THREE.ConeGeometry(0.5, 1, 6);
    const half = (7 * L.laneW * K) / 2;
    // The gorge's walls: the two plates, tall slate columns rising away from the pass.
    for (const h of L.walls) {
      const p = this.hex(h.q, h.r);
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      const out = Math.abs(p.x) - half;
      this.column(kit, prism, cap, p.x, p.z, 2.4 + Math.min(4, out * 0.9) + k * 1.4, k);
      // Ice in the seams of the plates now and then.
      if (k > 0.8) kit.add(cone, ICE[Math.floor(k * 3.99)] as string, place(p.x - Math.sign(p.x) * 0.4, this.ctx.ground(p.x, p.z) + 0.9, p.z, k * 4, new THREE.Vector3(0.3, 1.6, 0.3)));
    }
    // High at its head, the ice that shut it, split.
    const top = this.ctx.at(0, -9.6 * 866, 0);
    this.splitIce(kit, top.x, top.z, 2.6, 6.5);
    // Ruy's line: eleven sockets in the rock, notched; Harl's (the middle) wider.
    L.slots.forEach((p, i) => {
      const w = this.ctx.at(p.x, p.y, 0);
      kit.add(box, SLATE[4] as string, place(w.x, w.y + 0.03, w.z, 0, new THREE.Vector3(i === 5 ? 0.6 : 0.46, 0.08, 0.26)));
      kit.add(box, '#1c2129', place(w.x, w.y + 0.07, w.z, 0, new THREE.Vector3(i === 5 ? 0.42 : 0.3, 0.04, 0.1)));
      this.anchors.slots.push(new THREE.Vector3(w.x, w.y + 0.9, w.z));
    });
    // A line scored across the snow behind the sockets: what it must not cross.
    const a = this.ctx.at(L.slots[0]!.x, L.lineY + 260, 0);
    const b = this.ctx.at(L.slots[10]!.x, L.lineY + 260, 0);
    kit.add(box, '#7c8da3', place((a.x + b.x) / 2, a.y + 0.02, a.z, 0, new THREE.Vector3(Math.abs(b.x - a.x) + 0.8, 0.02, 0.05)));
    // The camp's fire by slot 1: a ring of stones, charred wood.
    const f = this.ctx.at(L.fire.x, L.fire.y, 0);
    for (let i = 0; i < 7; i++) {
      const t = (i / 7) * Math.PI * 2;
      kit.add(cap, SLATE[i % 4] as string, place(f.x + Math.cos(t) * 0.42, f.y + 0.06, f.z + Math.sin(t) * 0.42, t, 0.22));
    }
    for (let i = 0; i < 3; i++) kit.add(box, WOOD[i] as string, place(f.x, f.y + 0.08, f.z, i * 1.1, new THREE.Vector3(0.6, 0.07, 0.08)));
    this.light(new THREE.Vector3(f.x, f.y + 0.3, f.z), FIRE, 2.4, far ? 0 : 0.55);
    // The stretcher (the farms bring it up in the camp; it stays by the line after).
    const st = this.ctx.at(L.stretcher.x, L.stretcher.y, 0);
    for (const k of [-1, 1]) kit.add(box, WOOD[0] as string, place(st.x + k * 0.26, st.y + 0.16, st.z, 0, new THREE.Vector3(0.05, 0.05, 1.6)));
    kit.add(box, WOOD[1] as string, place(st.x, st.y + 0.19, st.z, 0, new THREE.Vector3(0.5, 0.03, 1.05)));
    this.anchors.stretcher = new THREE.Vector3(st.x, st.y + 0.8, st.z);
    const ruy = this.ctx.at(L.ruy.x, L.ruy.y, 0);
    this.anchors.ruy = new THREE.Vector3(ruy.x, ruy.y + 1.6, ruy.z);
    // The wedges' pile by the line: a crate of iron wedges.
    const pl = this.ctx.at(L.pile.x, L.pile.y, 0);
    kit.add(box, WOOD[2] as string, place(pl.x, pl.y + 0.18, pl.z, 0.3, new THREE.Vector3(0.7, 0.36, 0.5)));
    this.anchors.pile = new THREE.Vector3(pl.x, pl.y + 0.9, pl.z);
    this.anchors.banner = this.ctx.at(L.banner.x, L.banner.y, 1.9);
    // The beam up the snow side's wall: a long timber leaning to the cornice, a cross-piece at its foot, the basket.
    const ft = this.ctx.at(L.beam.x, L.beam.y, 0);
    const cn = this.ctx.at(L.cornice.x, L.cornice.y, 0);
    // The cornice's tip, out from the wall over the pass (the timber props it).
    const tip0 = Math.sign(ft.x) * (half + 0.2) + (cn.x - Math.sign(ft.x) * (half + 0.2)) * 0.62;
    const len = Math.hypot(tip0 - ft.x, cn.z - ft.z);
    const yaw = Math.atan2(tip0 - ft.x, cn.z - ft.z);
    // (Its tip under the cornice's tip, three and a half up: the timber leans from the basket to it.)
    const rise = 4.2;
    kit.add(box, WOOD[1] as string, place((ft.x + tip0) / 2, ft.y + rise / 2 + 0.3, (ft.z + cn.z) / 2, yaw, new THREE.Vector3(0.2, 0.2, Math.hypot(len, rise)), -Math.atan2(rise, len), 0));
    kit.add(box, WOOD[0] as string, place(ft.x, ft.y + 0.9, ft.z, yaw, new THREE.Vector3(0.14, 1.8, 0.14)));
    kit.add(box, WOOD[2] as string, place(ft.x, ft.y + 0.3, ft.z, 0, new THREE.Vector3(0.9, 0.5, 0.7)));
    this.anchors.basket = new THREE.Vector3(ft.x, ft.y + 1.4, ft.z);
    // The stones at its foot: three heaps, of 1, 2 and 3.
    L.stones.forEach((p, i) => {
      const w = this.ctx.at(p.x, p.y, 0);
      for (let k = 0; k < 3; k++) kit.add(cap, SLATE[(i + k) % 4] as string, place(w.x + (k - 1) * 0.22, w.y + 0.1 + 0.03 * i, w.z + (k % 2) * 0.15, k + i, 0.16 + i * 0.07));
      this.anchors.stones.push(new THREE.Vector3(w.x, w.y + 0.8, w.z));
    });
    // Ruy's cornice: a great lip of packed snow jutting from the snow side's wall over the pass, thick at the wall and
    // thinning to its tip, the beam propping its tip (its own group: it falls).
    const corn = new THREE.Group();
    const cm = this.std('#e6edf6', { roughness: 0.95 });
    const cd = this.std(SNOW_D, { roughness: 1 });
    const wallX = -Math.sign(cn.x || 1) * 0 + Math.sign(ft.x) * (half + 0.2);
    const tipX = wallX + (cn.x - wallX) * 0.62;
    for (let i = 0; i < 11; i++) {
      const t = i / 10;
      const lump = new THREE.Mesh(cap, i % 3 ? cm : cd);
      lump.position.set(wallX + (tipX - wallX) * t, cn.y + 4.6 - t * 0.6 + Math.sin(i * 2.3) * 0.1, cn.z + Math.sin(i * 1.7) * 0.5 + (i % 2) * 0.35);
      lump.scale.set(1.1 - t * 0.4, 0.8 - t * 0.35, 1.5 - t * 0.6);
      lump.castShadow = true;
      corn.add(lump);
    }
    // Its underside's shadow-blue lip and a few icicles.
    for (let i = 0; i < 6; i++) {
      const t = (i + 0.5) / 6;
      const ic = new THREE.Mesh(cone, cd);
      ic.position.set(wallX + (tipX - wallX) * t, cn.y + 4.0 - t * 0.5, cn.z + (i % 2) * 0.5 - 0.25);
      ic.scale.set(0.16, 0.7, 0.16);
      ic.rotation.x = Math.PI;
      corn.add(ic);
    }
    corn.name = 'pass-cornice';
    this.group.add(corn);
    this.cornice = corn;
    // The avalanche's three rocks, their grain a band of quartz leaning the way it turns the snow.
    for (const row of L.slide.rows)
      for (const rk of row) {
        const w = this.ctx.at(rk.x, rk.y, 0);
        kit.add(cap, SLATE[1] as string, place(w.x, w.y + 0.42, w.z, rk.lane, new THREE.Vector3(1.35, 1.05, 1.15)));
        kit.add(cap, SNOW, place(w.x, w.y + 0.86, w.z - 0.1, rk.lane + 1, new THREE.Vector3(0.95, 0.26, 0.8)));
        for (const off of [-0.12, 0.12]) kit.add(box, '#dfe6ee', place(w.x + off * 0.4, w.y + 0.45 + off, w.z + 0.52, 0, new THREE.Vector3(1.05, 0.07, 0.06), 0, -rk.dir * 0.55));
        this.anchors.rocks.push(new THREE.Vector3(w.x, w.y + 1.6, w.z));
      }
    // Far: Harl's shield standing in the middle of the empty pass, its red cloth in the wind.
    if (far) {
      const m = this.ctx.at(0, 866 * 2, 0);
      kit.add(box, '#8e8a84', place(m.x, m.y + 0.4, m.z, 0.2, new THREE.Vector3(0.5, 0.6, 0.06), -0.2));
      kit.add(box, COMPANY_RED, place(m.x + 0.1, m.y + 0.6, m.z + 0.1, 0.6, new THREE.Vector3(0.5, 0.12, 0.02), 0, 0.3));
      this.light(new THREE.Vector3(m.x, m.y + 0.5, m.z), RED, 1.2, 0.25);
    }
    // The shields (one instanced mesh: the line's ten or eleven, the stretcher's, Harl's in the snow).
    const sg = this.shieldGeo();
    if (sg) {
      this.geos.push(sg);
      const inst = new THREE.InstancedMesh(sg, this.std(undefined), 24);
      inst.count = 0;
      inst.castShadow = true;
      inst.name = 'pass-shields';
      inst.frustumCulled = false;
      this.group.add(inst);
      this.shields = inst;
    }
    // The small pieces: stones in the basket, wedges in the prints, the rocks and the pile (one instanced mesh).
    const bitGeo = this.track(new THREE.DodecahedronGeometry(0.2, 0));
    const bits = new THREE.InstancedMesh(bitGeo, this.std(undefined, { vertexColors: false, color: '#ffffff' }), 24);
    bits.count = 0;
    bits.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(72).fill(1), 3);
    bits.frustumCulled = false;
    bits.name = 'pass-bits';
    this.group.add(bits);
    this.bits = bits;
    // The prints of the next charge: two sinking hollows (decals), and the sweep's ring.
    const pg = this.track(new THREE.CircleGeometry(0.42, 16));
    pg.rotateX(-Math.PI / 2);
    for (let i = 0; i < 2; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: '#33456a', transparent: true, opacity: 0, depthWrite: false });
      this.mats.push(mat);
      const m = new THREE.Mesh(pg, mat);
      m.scale.set(0.8, 1, 1.3);
      m.renderOrder = 4;
      m.visible = false;
      this.group.add(m);
      this.prints.push(m);
    }
    const rg = this.track(new THREE.RingGeometry(0.9, 1, 40));
    rg.rotateX(-Math.PI / 2);
    const rm = new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0, depthWrite: false });
    this.mats.push(rm);
    this.sweep = new THREE.Mesh(rg, rm);
    this.sweep.visible = false;
    this.sweep.renderOrder = 5;
    this.group.add(this.sweep);
    // The lanes' shadow (the avalanche called): a strip from the rocks' rows down to the line.
    const lm = lanesMat();
    this.track(lm);
    const x0 = L.x0 * K;
    const lw = L.laneW * K;
    (lm.uniforms.uX0 as THREE.IUniform<number>).value = x0;
    (lm.uniforms.uLaneW as THREE.IUniform<number>).value = lw;
    const ztop = 1.6 * 866 * K;
    const zbot = (L.lineY + 400) * K;
    const lg = this.track(new THREE.PlaneGeometry(7 * lw, zbot - ztop));
    lg.rotateX(-Math.PI / 2);
    const lmesh = new THREE.Mesh(lg, lm);
    lmesh.position.set(0, this.ctx.ground(0, (ztop + zbot) / 2) + 0.06, (ztop + zbot) / 2);
    lmesh.visible = false;
    lmesh.renderOrder = 3;
    lmesh.frustumCulled = false;
    this.group.add(lmesh);
    this.lanes = { mesh: lmesh, mat: lm };
    prism.dispose();
    box.dispose();
    cone.dispose();
  }

  /* ---------- every tick, every frame ---------- */

  /** A tick's events: the shakes, the cornice falling, the line lit. */
  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      if (e.kind === 'gust' || e.kind === 'charge') this.ctx.shake(e.kind === 'charge' ? 0.4 : 0.15);
      else if (e.kind === 'trip') this.ctx.shake(0.3);
      else if (e.kind === 'avalanche') this.ctx.shake(0.5);
      else if (e.kind === 'broken') this.ctx.shake(0.7);
      else if (e.kind === 'cornice') {
        this.ctx.shake(0.6);
        if (this.corniceFall < 0) this.corniceFall = this.now;
      } else if (e.kind === 'lineRed') this.whole = this.now;
      else if (e.kind === 'read') this.flash = this.now;
    }
  }

  /** The scene's speech: the shield in this slot (1 to 11) lights red as its name is said (client game/cine.ts). */
  lightSlot(n: number): void {
    if (!this.lit.includes(n)) this.lit.push(n);
  }

  /** The close: Harl's shield in its gap, the whole line red. */
  closeLine(): void {
    if (!this.lit.includes(-1)) this.lit.push(-1);
    this.whole = this.now;
  }

  /** Every drawn frame: the shields in the line (and its give), the basket, the prints, the sweep, the lanes, the gust. */
  draw(s: BattleState): void {
    const st = s.senda;
    const crag = st?.crag;
    const rc = s.rules?.senda?.crag;
    if (crag && rc) {
      const t = rc.plan[crag.gust];
      this.state.gustWarn = t !== undefined && s.waveT >= t - rc.warn;
      this.state.gustOn = t !== undefined && s.waveT >= t - 3;
    }
    const L = this.L;
    const inst = this.shields;
    if (!L || !inst) return;
    let n = 0;
    const put = (x: number, y: number, h: number, yaw: number, tilt = 0): void => {
      if (n >= 24) return;
      const w = this.ctx.at(x, y, h);
      this.m.copy(place(w.x, w.y, w.z, yaw, 1.15, tilt));
      inst.setMatrixAt(n++, this.m);
    };
    const line = st?.line;
    const storm = st?.storm;
    const rs = s.rules?.senda?.storm;
    if (line) {
      line.on.forEach((who, i) => who >= 0 && put((L.slots[i] as { x: number }).x, (L.slots[i] as { y: number }).y, 0.1, Math.PI));
      // On the stretcher, the ones still on it, lying in a stack.
      let k = 0;
      line.pile.forEach((p) => {
        if (p !== 1) return;
        put(L.stretcher.x + ((k % 3) - 1) * 130, L.stretcher.y - 300 + Math.trunc(k / 3) * 330, 0.25 + Math.trunc(k / 3) * 0.02, Math.PI, -1.35);
        k++;
      });
    } else if (storm && rs) {
      L.slots.forEach((p, i) => {
        if (storm.holes[i]) return;
        put(p.x, p.y + (storm.back[i] ?? 0) * QUARTER, 0.1, Math.PI, Math.min(0.5, (storm.back[i] ?? 0) * 0.08));
      });
      if (storm.shield === 1) put(storm.shieldAt.x, storm.shieldAt.y, 0.1, Math.PI, 0.25);
    } else {
      // The beam's night and the scenes: the line as it was laid, Harl's gap in the middle (filled in the close).
      L.slots.forEach((p, i) => i !== 5 || this.lit.includes(-1) ? put(p.x, p.y, 0.1, Math.PI) : undefined);
    }
    inst.count = n;
    inst.instanceMatrix.needsUpdate = true;
    // The small pieces.
    const bits = this.bits;
    if (bits) {
      let b = 0;
      const bit = (x: number, y: number, h: number, sc: number, color: string, yaw = 0): void => {
        if (b >= 24) return;
        const w = this.ctx.at(x, y, h);
        bits.setMatrixAt(b, this.m.copy(place(w.x, w.y, w.z, yaw, sc)));
        bits.setColorAt(b, this.c.set(color));
        b++;
      };
      const beam = st?.beam;
      if (beam) beam.basket.forEach((v, i) => v > 0 && bit(L.beam.x + ((i % 2) - 0.5) * 260, L.beam.y + (i < 2 ? -120 : 120), 0.6, 0.55 + v * 0.22, SLATE[v] as string, i));
      if (storm && rs) {
        storm.prints.forEach((p, i) => storm.wedged === i && bit(p.x, p.y, 0.25, 0.9, IRON, 0.8));
        let k = 0;
        rs.rocks.flat().forEach((rk, i) => storm.shut[i] === 1 && bit(rk.x + rk.dir * 350, rk.y + 560, 0.4, 0.9, IRON, rk.dir));
        for (let i = 0; i < storm.pile; i++) bit(L.pile.x + ((k % 3) - 1) * 150, L.pile.y - 50 + Math.trunc(k++ / 3) * 120, 0.42, 0.6, IRON, i);
      }
      bits.count = b;
      bits.instanceMatrix.needsUpdate = true;
      if (bits.instanceColor) bits.instanceColor.needsUpdate = true;
    }
    // The prints, the sweep, the lanes.
    if (storm && rs) {
      this.state.prints = storm.prints.length > 0 && !storm.broken;
      storm.prints.forEach((p, i) => {
        const m = this.prints[i];
        if (!m) return;
        m.position.copy(this.ctx.at(p.x, p.y, 0.05));
        this.anchors.prints[i]?.copy(m.position).setY(m.position.y + 0.6);
      });
      this.state.sweep = storm.sweep >= 0;
      this.state.sx = storm.sx;
      this.state.sy = storm.sy;
      this.anchors.shield = storm.shield === 1 ? this.ctx.at(storm.shieldAt.x, storm.shieldAt.y, 1.4) : storm.shield === 2 ? (this.anchors.slots[5] ?? null) : null;
      this.state.fall = storm.fall >= 0 && !storm.fell;
      const lanes = this.lanes;
      if (lanes && this.state.fall) {
        const g = passFlow(rs.rocks, rs.snow, storm.shut);
        const arr = (lanes.mat.uniforms.uLanes as THREE.IUniform<number[]>).value;
        const hit = (lanes.mat.uniforms.uHit as THREE.IUniform<number[]>).value;
        for (let i = 0; i < 7; i++) {
          arr[i] = g[i] ?? 0;
          hit[i] = i >= 1 && i <= 5 && (g[i] ?? 0) > 0 ? 1 : 0;
        }
      }
      if (storm.breath > 0 || storm.corn >= 0) this.state.fall = this.state.fall || false;
      if (storm.stone && storm.corn < 0 && this.corniceFall < 0 && storm.breath === 0) this.corniceFall = this.now;
    }
    if (s.senda?.beam?.fallen && this.corniceFall < 0) this.corniceFall = this.now;
  }

  frame(now: number): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    if (this.gust) {
      (this.gust.uniforms.uTime as THREE.IUniform<number>).value = now % 120;
      const target = this.state.gustOn ? 1 : this.state.gustWarn ? 0.45 + 0.2 * Math.sin(now * 10) : 0;
      this.gustK += (target - this.gustK) * Math.min(1, dt * 5);
      (this.gust.uniforms.uK as THREE.IUniform<number>).value = this.gustK;
    }
    if (this.lanes) {
      const u = this.lanes.mat.uniforms;
      (u.uTime as THREE.IUniform<number>).value = now % 120;
      this.lanesK += ((this.state.fall ? 1 : 0) - this.lanesK) * Math.min(1, dt * 3);
      (u.uK as THREE.IUniform<number>).value = this.lanesK;
      this.lanes.mesh.visible = this.lanesK > 0.02;
    }
    const pulse = 0.5 + 0.5 * Math.sin(now * 5);
    for (const m of this.prints) {
      m.visible = this.state.prints;
      (m.material as THREE.MeshBasicMaterial).opacity = 0.35 + 0.25 * pulse;
    }
    if (this.sweep) {
      this.sweep.visible = this.state.sweep;
      if (this.state.sweep) {
        this.sweep.position.copy(this.ctx.at(this.state.sx, this.state.sy, 0.07));
        this.sweep.scale.setScalar(2.2 * K * 1000 * 0.001 * 1);
        (this.sweep.material as THREE.MeshBasicMaterial).opacity = 0.45 + 0.35 * pulse;
      }
    }
    // The cornice comes down: it drops and spreads over a second, then lies as a heap.
    if (this.cornice && this.corniceFall >= 0 && !this.corniceGone) {
      const k = Math.min(1, (now - this.corniceFall) / 1.1);
      this.cornice.position.y = -k * k * 4.2;
      this.cornice.scale.set(1 + k * 0.3, 1 - k * 0.55, 1 + k * 0.3);
      if (k >= 1) this.corniceGone = true;
    }
    this.glow.begin();
    for (const gl of this.glows) {
      const f = 0.9 + Math.sin(now * 1.3 + gl.seed) * 0.1;
      this.glow.add(gl.p.x, gl.p.y, gl.p.z, gl.c, gl.size, gl.a * f);
    }
    // The shields lit red: by name (the speech), all of them (the line whole), one read back (Wenn).
    const L = this.L;
    if (L) {
      const whole = this.whole >= 0;
      const wk = whole ? Math.min(1, (now - this.whole) / 1.2) : 0;
      L.slots.forEach((p, i) => {
        const on = this.lit.includes(i + 1) || whole;
        if (!on) return;
        // Before its face (toward the pass, where the cross is) and a halo over its rim, so it reads from either side.
        const w = this.ctx.at(p.x, p.y - 260, 0.45);
        const k = whole ? wk : 1;
        this.glow.add(w.x, w.y, w.z, RED, 1.05, (0.42 + 0.12 * Math.sin(now * 3 + i)) * k);
        this.glow.add(w.x, w.y + 0.55, w.z + 0.2, RED_SOFT, 0.8, 0.22 * k);
        this.glow.add(w.x, w.y - 0.4, w.z, RED_SOFT, 1.6, 0.12 * k);
      });
      if (this.flash && now - this.flash < 1.2) {
        const b = this.anchors.banner;
        if (b) this.glow.add(b.x, b.y, b.z, COLD, 1.5, 0.5 * (1 - (now - this.flash) / 1.2));
      }
      const sh = this.anchors.shield;
      if (sh) this.glow.add(sh.x, sh.y - 0.6, sh.z, RED, 1.4, 0.35 + 0.25 * pulse);
    }
    this.glow.end();
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
  }
}
